import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, Stack, Chip, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import usePortfolioStore from '../store/portfolioStore.js';
import { Link } from 'react-router-dom';
import Particles from '../components/Particles.jsx';
import Lottie from 'lottie-react';

export default function Home() {
  const { about, liveSites, fetchRepos } = usePortfolioStore();
  const getPinnedProjects = usePortfolioStore((s) => s.getPinnedProjects);
  useEffect(() => { fetchRepos(); }, [fetchRepos]);
  const [lottieData, setLottieData] = useState(null);
  useEffect(() => {
    const url = import.meta.env.VITE_LOTTIE_URL;
    if (!url) return;
    fetch(url).then(r => r.json()).then(setLottieData).catch(() => { });
  }, []);

  return (
    <Box>
      <Box position="relative" overflow="hidden" sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, borderRadius: 2 }}>
        {lottieData ? (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.4 }}>
            <Lottie animationData={lottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
          </div>
        ) : (
          <Particles />
        )}
        <Box position="relative">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Avatar src={'/Mesum.jpg'} alt="Mesum Bin Shaukat" sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }} />
            <Typography variant="overline" sx={{ opacity: .8, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Available for full‑stack, AI, and e‑commerce projects</Typography>
          </Stack>
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
            {about.headline}
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 900, opacity: .9 }}>
            {about.summary}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" color="primary" component={Link} to="/projects" fullWidth={false}>View Projects</Button>
            <Button variant="outlined" color="primary" component={Link} to="/about" fullWidth={false}>About Me</Button>
            <Button variant="outlined" color="primary" component={Link} to="/skills" fullWidth={false}>Explore Skills</Button>
          </Stack>
        </Box>
      </Box>

      <Box className="section">
        <Typography variant="h5" gutterBottom>Featured Projects</Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {getPinnedProjects().length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ opacity: .8 }}>Pin projects from the Projects page or Admin to feature them here.</Typography>
            </Grid>
          )}
          {getPinnedProjects().map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <motion.div whileHover={{ y: -6, scale: 1.01 }}>
                <Card className="glass">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" gutterBottom>{r.name}</Typography>
                      <Chip size="small" color="primary" label="Pinned" />
                    </Stack>
                    <Typography variant="body2" sx={{ minHeight: 48, opacity: .9 }}>{r.description || '—'}</Typography>
                    <Stack direction="row" spacing={1} sx={{ my: 1 }}>
                      {(r.topics || []).slice(0, 3).map(t => <Chip key={t} size="small" label={t} />)}
                      {r.language && <Chip size="small" label={r.language} />}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button href={r.url} target="_blank" rel="noreferrer" size="small">Repository</Button>
                      {r.homepage && <Button href={r.homepage} target="_blank" rel="noreferrer" size="small">Live</Button>}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" gutterBottom>Featured Live Websites</Typography>
        <Grid container spacing={2}>
          {liveSites.slice(0, 8).map((url, i) => (
            <Grid item xs={12} sm={6} md={3} key={url}>
              <motion.div whileHover={{ y: -6, scale: 1.02 }} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Card className="glass" sx={{ position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(144,202,249,0.08), transparent 60%)', opacity: 0, transition: 'opacity .3s' }} className="card-shimmer" />
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom noWrap>{url.replace('https://', '').replace('http://', '')}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button href={url} target="_blank" rel="noreferrer" size="small">Visit</Button>
                      <Button component={Link} to="/projects" size="small" variant="outlined">Details</Button>
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box className="section">
        <Typography variant="h5" gutterBottom>Services</Typography>
        <Grid container spacing={2}>
          {[{
            title: 'Custom Web Architecture',
            desc: 'Scalable, high-performance web apps using React, Next.js, and modern Node.js ecosystems.'
          }, {
            title: 'AI Solutions & Integration',
            desc: 'Deploying RAG pipelines, AI agents, and workflow automations to drive business efficiency.'
          }, {
            title: 'Enterprise E-Commerce',
            desc: 'Headless Shopify/Magento architectures, custom payment flows, and high-conversion UX.'
          }, {
            title: 'Backend & API Development',
            desc: 'Robust REST/GraphQL APIs, microservices, and secure auth systems using NestJS or .NET.'
          }, {
            title: 'Cloud Infrastructure',
            desc: 'DevOps best practices, CI/CD pipelines, Docker containerization, and AWS/Azure management.'
          }, {
            title: 'Performance & SEO',
            desc: 'Technical SEO audits, Core Web Vitals optimization, and accessibility compliance.'
          }].map(card => (
            <Grid key={card.title} item xs={12} md={4}>
              <motion.div whileHover={{ y: -6, scale: 1.01 }}>
                <Card className="glass">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{card.title}</Typography>
                    <Typography variant="body2" sx={{ opacity: .85 }}>{card.desc}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}


